import { message } from 'antd';
import { curry, set } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ParsedForm } from 'store/declaration-form-errors/ParsedForm';
import { h1Box44KeyNames, h1PathBox44 } from 'views/declarations/common/box44/mappers';

import {
    getDeclarationItems,
    getDeclarationPayload,
    getDeclarationPayloadItems,
} from 'views/declarations/utils/declaration-utils';
import { Events } from 'utils/events.enum';
import { extractDeclarationInvoiceData } from 'store/declarations/client';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import useHideEntities from 'hooks/useHideEntities';
import { SModal } from 'views/templates/styles';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import IrelandImportDeclarationUtils, { makeTransformation } from '../../utils';
import { getInvoiceConfig, SUGGESTED_TARICS_NUMERATION_REGEX } from './invoice-utils';
import useInvoiceReducer from './reducer/useInvoiceReducer';
import { complexSet } from 'utils/complexGetSet';
import h1Validation from '../validation/ValidationSchema';
import InvoiceBody from './InvoiceBody';
import InvoiceHeader from './InvoiceHeader';

const REFRESH_DELAY_TIME = 10000;

export enum TaricEvalStatus {
    BASIC_INFO_RECEIVED = 'BASIC_INFO_RECEIVED',
    TARIC_SUGGESTIONS_RECEIVED = 'TARIC_SUGGESTIONS_RECEIVED',
    TEE_UPDATED_WITH_SUBMITTED_DATA = 'TEE_UPDATED_WITH_SUBMITTED_DATA',
    FAILED = 'FAILED',
    ACCEPTED = 'ACCEPTED',
}

interface Props {
    formikValues?: any;
    declarationId?: string;
    saveAsDraft?: Function;
}

export type CustomerLevelValidationData = {
    eoriLevelPaths: Record<string, any>;
    addressLevelPaths: Record<string, any>;
    allCustomerValues: Record<string, any>;
    customerHeader: string;
    customerPath: string;
    disabled: boolean;
};

const EORI_LEVEL_FIELD_PATHS = ['eori', 'identificationNumber', 'id', 'statusCode', 'functionCode'];

export type LMap = Map<string, string | number | boolean | string[] | undefined>;

// TEE | TARIC Evaluation Engine
const InvoiceModal = ({ formikValues, declarationId, saveAsDraft }: Props) => {
    const { isFormType } = useHideEntities();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [activeForm, setActiveForm] = useState<'master' | 'product'>('master');
    const [parsedInvoiceResponse, setParsedInvoiceResponse] = useState<ParsedForm | undefined>(undefined);
    const [selectedSuggestedValuesMap, setSelectedSuggestedValuesMap] = useState<LMap>(new Map());
    const [selectedCustomers, setSelectedCustomers] = useState<Record<string, string[]>>({});
    const [selectedProductsActions, setSelectedProductsActions] = useState<Record<string, string | undefined>>({});
    const [errors, setErrors] = useState<any>(null);

    const [TEE, dispatch] = useInvoiceReducer();

    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { parseForm, declarationType, itemPath } = useGetDeclarationMapValues();

    const isFetchingRef = useRef(false);

    const invoiceConfig = useMemo(() => {
        const invoiceConfig = getInvoiceConfig(isFormType);
        return invoiceConfig[declarationType as keyof typeof invoiceConfig];
    }, [declarationType, isFormType]);

    const getInvoiceData = useCallback(async () => {
        if (!declarationId || isFetchingRef.current) return;

        isFetchingRef.current = true;

        try {
            const declarationData = await extractDeclarationInvoiceData(declarationId);
            dispatch({
                type: 'UPDATE_STATUS',
                status: declarationData.taricEvalStatus,
            });

            const items = getDeclarationItems(declarationData);
            let declarationPayload = getDeclarationPayload(declarationData);

            if (!declarationPayload) {
                return;
            }

            const newDeclarationPayload = makeTransformation(
                declarationPayload,
                curry(IrelandImportDeclarationUtils.box44Transform)(
                    IrelandImportDeclarationUtils.box44ToObject,
                    h1PathBox44,
                    h1Box44KeyNames
                )
            );

            dispatch({ type: 'SET_DECLARATION', payload: newDeclarationPayload, items });
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description:
                    errorMessage ||
                    'Error generating the invoice! Please create a new declaration and attach an invoice to it.',
                placement: 'top',
            });
        } finally {
            isFetchingRef.current = false;
        }
    }, [declarationId, dispatch]);

    useEffect(() => {
        if (
            TEE.status === TaricEvalStatus.TARIC_SUGGESTIONS_RECEIVED ||
            TEE.status === TaricEvalStatus.TEE_UPDATED_WITH_SUBMITTED_DATA ||
            TEE.status === TaricEvalStatus.FAILED
        )
            return;

        getInvoiceData();

        const interval = setInterval(() => {
            getInvoiceData();
        }, REFRESH_DELAY_TIME);

        return () => clearInterval(interval);
    }, [getInvoiceData, TEE.status]);

    useEffect(() => {
        (async () => {
            if (!TEE.payload) return;

            TEE.payload.validationRequired = false;
            // TEE.payload.irelandImportDeclaration.goodsShipment.goodsShipmentItem.countryOrigin = 'Poland';
            let errors: any;
            if (declarationType === 'irelandImportDeclaration') {
                await h1Validation()
                    .validate(TEE.payload, { abortEarly: false })
                    .catch((err) => {
                        errors = err;
                    });
            }

            const defaultParse = parseForm?.(TEE.payload, {
                withoutCustomers: true,
            }) as ParsedForm;

            const invoiceParse = invoiceConfig.parser(defaultParse, TEE.payload);

            setParsedInvoiceResponse(invoiceParse);
            setErrors(errors);
            window.EventBus.publish(Events.HANDLE_INVOICE_MODAL, () => setModalOpen(true));
        })();
    }, [TEE.payload, declarationType, invoiceConfig, parseForm]);

    useEffect(() => {
        if (TEE.status === TaricEvalStatus.FAILED) window.EventBus.publish(Events.INVOICE_FAILED_INITIALLY);
    }, [TEE.status]);

    const handleCloseModal = useCallback(() => setModalOpen(false), [setModalOpen]);

    const handleChangeForm = useCallback((form: 'master' | 'product') => setActiveForm(form), []);

    const getTaricEvalLineItemId = useCallback(
        (productPath: `product${number}`) => {
            if (!TEE.payload) return null;
            const indexOfInvoiceProduct = +productPath.slice(7);

            const taricEvalLineItemId = TEE.items?.[indexOfInvoiceProduct]?.taricEvalLineItemId;

            return taricEvalLineItemId;
        },
        [TEE.items, TEE.payload]
    );

    const setInvoiceProductsInFormikStructure = useCallback(
        (suggestedValuesFormikStructure: any) => {
            let indexOfProductForCreation = 0;
            let pathOfProduct: string;
            Object.entries(selectedProductsActions).forEach(([productPath, productAction]) => {
                let bodyOfProduct: Record<string, string> = {};
                const productActionIsNotSelected = !(productPath in selectedProductsActions);
                const productActionIsUndefined = selectedProductsActions[productPath] === undefined;

                if (productActionIsNotSelected || productActionIsUndefined) return;

                if (productAction === '') {
                    const declarationProductsLength = getDeclarationPayloadItems(formikValues)?.length;

                    const indexToCreateProductOn = declarationProductsLength
                        ? declarationProductsLength + indexOfProductForCreation
                        : indexOfProductForCreation;

                    pathOfProduct = `${itemPath}.[${indexToCreateProductOn}]`;

                    indexOfProductForCreation++;
                } else if (productAction !== '' && productAction) {
                    const productActionValues = productAction.split(' ');
                    pathOfProduct = productActionValues[0];
                    const idOfProductForUpdate = productActionValues[1];

                    bodyOfProduct = {
                        id: idOfProductForUpdate,
                    };
                }

                bodyOfProduct = {
                    ...bodyOfProduct,
                    ...suggestedValuesFormikStructure[productPath],
                    taricEvalLineItemId: getTaricEvalLineItemId(productPath as `product${number}`),
                };

                // NCTS specific
                if (declarationType === 'ieNctsDeclaration') {
                    let houseConsignmentId = formikValues?.consignment?.houseConsignment?.[0]?.id;

                    if (bodyOfProduct.id) {
                        houseConsignmentId = formikValues?.consignment?.houseConsignment?.find(
                            (houseConsignment: any) => {
                                return houseConsignment.consignmentItem.find((consignmentItem: any) => {
                                    return consignmentItem.id === bodyOfProduct.id;
                                });
                            }
                        )?.id;
                    }

                    bodyOfProduct = {
                        ...bodyOfProduct,
                        houseConsignmentId,
                    };
                }

                complexSet(suggestedValuesFormikStructure, pathOfProduct, bodyOfProduct);
                delete suggestedValuesFormikStructure[productPath];
            });
        },
        [selectedProductsActions, getTaricEvalLineItemId, declarationType, formikValues, itemPath]
    );

    const setCustomerInFormikStructureAndGetLevelValidation = useCallback(
        (suggestedValuesFormikStructure: any): CustomerLevelValidationData[] => {
            const customersLevelValidationData: CustomerLevelValidationData[] = [];

            Object.entries(selectedCustomers).forEach(([invoiceCustomer, selectedCustomerPaths]) => {
                const selectedCustomerValues = suggestedValuesFormikStructure[invoiceCustomer];

                if (!selectedCustomerValues) return;

                let customerLevelValidationData = {
                    eoriLevelPaths: {},
                    addressLevelPaths: {},
                    allCustomerValues: selectedCustomerValues,
                } as CustomerLevelValidationData;

                Object.keys(selectedCustomerValues).forEach((fieldPath, i) => {
                    if (EORI_LEVEL_FIELD_PATHS.includes(fieldPath))
                        customerLevelValidationData.eoriLevelPaths[i] = fieldPath;
                    else customerLevelValidationData.addressLevelPaths[i] = fieldPath;
                });

                selectedCustomerPaths.forEach((customerPath: string) => {
                    customerLevelValidationData = { ...customerLevelValidationData };
                    customerLevelValidationData.customerPath = customerPath;
                    customerLevelValidationData.customerHeader =
                        invoiceConfig.partiesDataStructure.find((partyData) => partyData.path === customerPath)
                            ?.header ?? 'Customer';
                    customersLevelValidationData.push(customerLevelValidationData);

                    set(suggestedValuesFormikStructure, customerPath, selectedCustomerValues);
                });
            });

            Object.keys(selectedCustomers).forEach((key) => delete suggestedValuesFormikStructure[key]);

            return customersLevelValidationData;
        },
        [invoiceConfig.partiesDataStructure, selectedCustomers]
    );

    const getPath = (suggestedValuePath: string) => {
        const [_, path] = suggestedValuePath.split('-');
        return path;
    };

    const setSuggestedValue = (structure: any, path: string, value: unknown) => {
        // Box44 form values are keyed objects ({ documentType, documentIdentifier }), not string arrays.
        // Applying only the identifier path would omit documentType, so write the full entry.
        const identifierSuffix = `.${h1Box44KeyNames.documentIdentifier}`;
        if (path.includes('producedDocumentsWritingOff') && path.endsWith(identifierSuffix)) {
            const documentPath = path.slice(0, -identifierSuffix.length);
            const documentType = documentPath.split('.').at(-1);
            if (documentType) {
                return set(structure, documentPath, {
                    [h1Box44KeyNames.documentType]: documentType,
                    [h1Box44KeyNames.documentIdentifier]: value,
                });
            }
        }

        return set(structure, path, value);
    };

    const handleSave = useCallback(async () => {
        if (!selectedSuggestedValuesMap || selectedSuggestedValuesMap.size === 0)
            return message.error('Please select suggested values in order to save.');

        let suggestedValuesFormikStructure: any = {};
        for (let [key, value] of selectedSuggestedValuesMap) {
            if (invoiceConfig.taricPaths.some((path) => key.includes(path)))
                key = key.replace(SUGGESTED_TARICS_NUMERATION_REGEX, '');
            else if (key.includes('containerIdentificationNumber'))
                value = (value as string[])?.map((containerIdentificationNumber) =>
                    containerIdentificationNumber.slice(0, 16)
                );

            let path = key;

            if (key?.includes('customer') || key?.includes('product')) {
                path = getPath(key);
            }

            suggestedValuesFormikStructure = setSuggestedValue(suggestedValuesFormikStructure, path, value);
        }

        setInvoiceProductsInFormikStructure(suggestedValuesFormikStructure);
        const customerLevelValidation =
            setCustomerInFormikStructureAndGetLevelValidation(suggestedValuesFormikStructure);

        try {
            showGlobalOverlay({
                type: 'LoadingOverlay',
            });

            await saveAsDraft?.(true, suggestedValuesFormikStructure);
            window.EventBus.publish(Events.INVOICE_MODAL_SAVED, {
                customerLevelValidation,
            });
        } finally {
            hideGlobalOverlay();
        }

        handleCloseModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSuggestedValuesMap, selectedCustomers, setInvoiceProductsInFormikStructure]);

    const itemDetailsDisabled = useMemo(() => {
        return TEE.items?.length === 0;
    }, [TEE.items]);

    return (
        <SModal
            open={modalOpen}
            onCancel={handleCloseModal}
            width={'100rem'}
            footer={false}
            closable={false}
            destroyOnClose
            maskClosable={false}
        >
            <InvoiceHeader
                handleClose={handleCloseModal}
                handleChangeForm={handleChangeForm}
                activeForm={activeForm}
                onSave={handleSave}
                itemDetailsDisabled={itemDetailsDisabled}
            />
            <InvoiceBody
                activeForm={activeForm}
                parsedInvoiceResponse={parsedInvoiceResponse}
                formikValues={formikValues}
                selectedSuggestedValuesMap={selectedSuggestedValuesMap}
                setSelectedSuggestedValues={setSelectedSuggestedValuesMap}
                setSelectedCustomers={setSelectedCustomers}
                selectedCustomers={selectedCustomers}
                setSelectedProductAction={setSelectedProductsActions}
                selectedProductsActions={selectedProductsActions}
                failedToGetSuggestedTarics={TEE.status === TaricEvalStatus.FAILED}
                itemsPath={itemPath}
                partiesDataStructure={invoiceConfig.partiesDataStructure}
                taricPaths={invoiceConfig.taricPaths}
                errors={errors}
            />
        </SModal>
    );
};

export default InvoiceModal;
